const API_URL = import.meta.env.VITE_API_URL ?? "/api/v1";

function cookie(name: string) {
  return document.cookie.split("; ").find((part) => part.startsWith(`${name}=`))?.split("=").slice(1).join("=");
}

export class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

function errorMessage(detail: unknown): string {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => {
      if (!item || typeof item !== "object") return String(item);
      const error = item as { loc?: unknown[]; msg?: string };
      const field = error.loc?.filter((part) => part !== "body").join(".");
      return field ? `${field}: ${error.msg ?? "valor inválido"}` : (error.msg ?? "Valor inválido");
    }).join("; ");
  }
  return "Não foi possível concluir.";
}

async function request(path: string, options: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const cancel = () => controller.abort(options.signal?.reason);
  if (options.signal?.aborted) cancel();
  else options.signal?.addEventListener("abort", cancel, { once: true });
  // Checkout can perform several sequential calls to the payment provider.
  const timeout = window.setTimeout(() => controller.abort(), path === "/payments/checkout" ? 120_000 : 30_000);
  try {
    return await fetch(`${API_URL}${path}`, { ...options, signal: controller.signal, credentials: "include" });
  } catch (error) {
    if (options.signal?.aborted) throw error;
    if (controller.signal.aborted) throw new Error("A solicitação demorou demais. Tente novamente.");
    throw new Error("Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.");
  } finally {
    window.clearTimeout(timeout);
    options.signal?.removeEventListener("abort", cancel);
  }
}

export async function api<T>(path: string, options: RequestInit = {}, responseType: "json" | "blob" = "json"): Promise<T> {
  const method = options.method?.toUpperCase() ?? "GET";
  const headers = new Headers(options.headers);
  if (options.body && !(options.body instanceof FormData)) headers.set("Content-Type", "application/json");
  const updateCsrf = () => {
    if (["GET", "HEAD", "OPTIONS"].includes(method)) return;
    const csrf = cookie("csrf_token");
    if (csrf) headers.set("X-CSRF-Token", decodeURIComponent(csrf));
    else headers.delete("X-CSRF-Token");
  };
  updateCsrf();
  let response = await request(path, { ...options, headers });
  if (response.status === 401 && !["/auth/refresh", "/auth/login", "/auth/register"].includes(path)) {
    const refreshed = await request("/auth/refresh", { method: "POST", signal: options.signal });
    if (refreshed.ok) {
      updateCsrf();
      response = await request(path, { ...options, headers });
    }
  }
  if (!response.ok) {
    const payload = await response.json().catch(() => ({ detail: "Não foi possível concluir." }));
    throw new ApiError(response.status, response.status === 401 && !path.startsWith("/auth/")
      ? "Sua sessão expirou. Entre novamente para continuar."
      : errorMessage(payload?.detail));
  }
  if (response.status === 204) return undefined as T;
  if (responseType === "blob") return response.blob() as Promise<T>;
  return response.json() as Promise<T>;
}
